import { resolveColour, type ColourRoleName, type PaletteName } from "odyssey-shared";
import { HEALTH_BAR } from "./bar.ts";

/**
 * The locked segment of a character's health bar (ADR-079's amendment of 2026-10-03 for P2.2-T16): the part of the
 * full maximum a maximum-health penalty removes, drawn at the bar's right end inside its edge, flat in `vellum-400`,
 * the colour the card's segment takes. It is a second image over the bar, its frame cut from one strip drawn once
 * holding the segment at every width from 0 to the inside, transparent elsewhere, so a change of penalty changes the
 * frame and nothing is drawn again; the bar's own strip stays one frame per fill.
 */

/**
 * The texture key the strip is drawn into.
 */
export const LOCKED_BAR_TEXTURE = "health-bar-locked";

/**
 * The palette entry the locked segment is drawn in.
 */
export const LOCKED_ROLE: PaletteName | ColourRoleName = "vellum-400";

const FRAMES: readonly string[] = Object.freeze(Array.from({ length: HEALTH_BAR.inside + 1 }, (_, locked) => `${LOCKED_BAR_TEXTURE}/${locked}`));

/**
 * How many frames the strip holds: one for each locked width from 0 to the inside.
 */
export const LOCKED_BAR_FRAMES = FRAMES.length;

/**
 * The strip's frame for a locked width; the names are made once.
 *
 * @param locked The locked width in logical pixels, 0 to the inside.
 * @returns The frame's name.
 */
export function lockedFrame(locked: number): string {
  return FRAMES[Math.min(HEALTH_BAR.inside, Math.max(0, locked))] as string;
}

/**
 * Every frame's name and its row in the strip, in the strip's order, for cutting the frames from the texture.
 *
 * @param each Called with each frame's name and the index of its cell from the top.
 */
export function forEachLockedFrame(each: (name: string, index: number) => void): void {
  FRAMES.forEach((name, index) => each(name, index));
}

/**
 * The strip every locked segment's frame is cut from: one cell of the bar's size per width from 0 to the inside,
 * stacked from the top, each transparent but for the inside's last `width` columns, both rows, in `LOCKED_ROLE`.
 *
 * @returns The strip's size and its RGBA pixels.
 */
export function lockedBarStrip(): { readonly width: number; readonly height: number; readonly pixels: Uint8ClampedArray } {
  const { width, height, inside } = HEALTH_BAR;
  const hex = resolveColour(LOCKED_ROLE);
  const colour = [Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16)] as const;
  const pixels = new Uint8ClampedArray(width * height * LOCKED_BAR_FRAMES * 4);
  for (let locked = 0; locked <= inside; locked += 1) {
    for (let row = 1; row < height - 1; row += 1) {
      for (let x = width - 1 - locked; x < width - 1; x += 1) {
        const at = ((locked * height + row) * width + x) * 4;
        pixels[at] = colour[0];
        pixels[at + 1] = colour[1];
        pixels[at + 2] = colour[2];
        pixels[at + 3] = 255;
      }
    }
  }
  return { width, height: height * LOCKED_BAR_FRAMES, pixels };
}
